import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import * as E from '@react-email/components'
import { data, redirect, Form, Link } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { verifyMember } from '#app/utils/plq-member-api.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { verifySessionStorage } from '#app/utils/verification.server.ts'
import { type Route } from './+types/signup.ts'
import {
	plqSignupDataSessionKey,
	type PLQSignupSessionData,
} from './onboarding/index.tsx'
import { prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

// Zod schemas for the 5 fields collected at signup (email + identity +
// 3 PLQ verification factors). Kept inline in this file rather than in
// user-validation.ts because they're signup-specific.
const FirstNameSchema = z
	.string({ required_error: 'Le prénom est requis' })
	.trim()
	.min(1, { message: 'Le prénom est requis' })
	.max(100, { message: 'Le prénom est trop long' })

const LastNameSchema = z
	.string({ required_error: 'Le nom de famille est requis' })
	.trim()
	.min(1, { message: 'Le nom de famille est requis' })
	.max(100, { message: 'Le nom de famille est trop long' })

const MemberIdSchema = z
	.string({ required_error: 'Le numéro de membre est requis' })
	.trim()
	.regex(/^\d+$/, {
		message: 'Le numéro de membre doit être un entier positif',
	})
	.transform((v) => Number(v))
	.refine((v) => Number.isInteger(v) && v > 0 && v <= 2_147_483_647, {
		message: 'Le numéro de membre est hors de la plage valide',
	})

// Canadian postal code: letter-digit-letter (space optional) digit-letter-digit.
// The PLQ API normalizes spaces + case server-side, but we reject obvious
// garbage client-side so the user gets a clear error before hitting the API.
const PostalCodeSchema = z
	.string({ required_error: 'Le code postal est requis' })
	.trim()
	.regex(/^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/, {
		message: 'Le code postal doit avoir le format A1A 1A1',
	})

const SignupSchema = z.object({
	email: EmailSchema,
	firstName: FirstNameSchema,
	lastName: LastNameSchema,
	memberId: MemberIdSchema,
	postalCode: PostalCodeSchema,
})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return null
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()

	await checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema,
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { email, firstName, lastName, memberId, postalCode } = submission.value

	// Verify the 3 PLQ membership factors with the external dashboard API
	// BEFORE any duplicate-account check (email OR plqMemberId). This
	// ordering is important for two reasons:
	//
	//   1. Non-members can't trigger outbound verification email traffic
	//      through the signup form.
	//   2. An attacker can't enumerate which emails / memberIds are
	//      already enrolled without first passing the 3-factor check —
	//      both uniqueness checks happen *after* identity is confirmed.
	const verifyResult = await verifyMember({ memberId, lastName, postalCode })

	if (verifyResult.status === 'invalid') {
		return data(
			{
				result: submission.reply({
					formErrors: [
						"Nous n'avons pas pu vérifier votre adhésion au PLQ avec ces informations. Veuillez vérifier votre numéro de membre, votre nom de famille et votre code postal.",
					],
				}),
			},
			{ status: 400 },
		)
	}
	if (verifyResult.status === 'error') {
		return data(
			{
				result: submission.reply({
					formErrors: [
						"Nous éprouvons actuellement des difficultés à joindre le registre d'adhésion du PLQ. Veuillez réessayer dans un instant.",
					],
				}),
			},
			{ status: 503 },
		)
	}

	// Identity confirmed. Run both duplicate-account checks in parallel —
	// the user only sees one message at a time, but running them together
	// keeps the action's latency budget tight.
	const [existingByEmail, existingByMember] = await Promise.all([
		prisma.user.findUnique({
			where: { email },
			select: { id: true },
		}),
		prisma.user.findUnique({
			where: { plqMemberId: verifyResult.member.id },
			select: { id: true },
		}),
	])
	if (existingByEmail) {
		return data(
			{
				result: submission.reply({
					fieldErrors: {
						email: ['Un compte existe déjà avec ce courriel'],
					},
				}),
			},
			{ status: 400 },
		)
	}
	if (existingByMember) {
		return data(
			{
				result: submission.reply({
					formErrors: [
						'Un compte existe déjà pour ce membre PLQ. Essayez plutôt de vous connecter.',
					],
				}),
			},
			{ status: 400 },
		)
	}

	// Stash the confirmed membership + full name in the verifySession so
	// that the onboarding action can persist everything onto the User once
	// the email code is validated. The verifySession cookie has a 10-minute
	// TTL (matches the TOTP).
	const verifySession = await verifySessionStorage.getSession(
		request.headers.get('cookie'),
	)
	const sessionData: PLQSignupSessionData = {
		plqMemberId: verifyResult.member.id,
		echeance: verifyResult.member.echeance,
		name: `${firstName} ${lastName}`,
	}
	verifySession.set(plqSignupDataSessionKey, sessionData)

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
	})

	const response = await sendEmail({
		to: email,
		subject: 'Bienvenue à WebApp PLQ !',
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString(), {
			headers: {
				'set-cookie': await verifySessionStorage.commitSession(verifySession),
			},
		})
	} else {
		return data(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}

export function SignupEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="fr" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Bienvenue à WebApp PLQ !</E.Text>
				</h1>
				<p>
					<E.Text>
						Voici votre code de vérification : <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Ou cliquez sur le lien pour continuer :</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Inscription | WebApp PLQ' }]
}

export default function SignupRoute({ actionData }: Route.ComponentProps) {
	const isPending = useIsPending()

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container flex flex-col justify-center pt-8 pb-16">
			<div className="text-center">
				<h1 className="text-h4">Activer votre compte</h1>
				<p className="text-body-sm text-muted-foreground mt-2">
					Nous vérifierons votre adhésion au PLQ, puis vous enverrons un code
					par courriel pour finaliser la création de votre compte.
				</p>
			</div>
			<div className="mx-auto mt-6 max-w-sm min-w-full sm:min-w-[368px]">
				<Form method="POST" {...getFormProps(form)}>
					<HoneypotInputs />
					<Field
						labelProps={{
							htmlFor: fields.email.id,
							children: 'Courriel',
						}}
						inputProps={{
							...getInputProps(fields.email, { type: 'email' }),
							autoFocus: true,
							autoComplete: 'email',
						}}
						errors={fields.email.errors}
					/>
					<Field
						labelProps={{
							htmlFor: fields.firstName.id,
							children: 'Prénom',
						}}
						inputProps={{
							...getInputProps(fields.firstName, { type: 'text' }),
							autoComplete: 'given-name',
						}}
						errors={fields.firstName.errors}
					/>
					<Field
						labelProps={{
							htmlFor: fields.lastName.id,
							children: 'Nom de famille',
						}}
						inputProps={{
							...getInputProps(fields.lastName, { type: 'text' }),
							autoComplete: 'family-name',
						}}
						errors={fields.lastName.errors}
					/>
					<Field
						labelProps={{
							htmlFor: fields.memberId.id,
							children: 'Numéro de membre PLQ',
						}}
						inputProps={{
							...getInputProps(fields.memberId, { type: 'text' }),
							inputMode: 'numeric',
							autoComplete: 'off',
						}}
						errors={fields.memberId.errors}
					/>
					<Field
						labelProps={{
							htmlFor: fields.postalCode.id,
							children: 'Code postal',
						}}
						inputProps={{
							...getInputProps(fields.postalCode, { type: 'text' }),
							autoComplete: 'postal-code',
							placeholder: 'A1A 1A1',
						}}
						errors={fields.postalCode.errors}
					/>
					<ErrorList errors={form.errors} id={form.errorId} />
					<StatusButton
						className="w-full"
						status={isPending ? 'pending' : (form.status ?? 'idle')}
						type="submit"
						disabled={isPending}
					>
						Continuer
					</StatusButton>
				</Form>
				<div className="flex items-center justify-center pt-3">
					<p className="text-muted-foreground text-center text-sm">
						Déjà un compte ?{' '}
						<Link
							to="/login"
							className="text-primary font-medium underline underline-offset-4"
						>
							Se connecter
						</Link>
					</p>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
